import { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import "./ContractRegister.css";
import axiosInstance from '../../../Config/axiosInstance';
import NotAuthorized from '../../../Components/ProtectedRoute/NotAuthorized';

import { CircularProgress } from '@mui/material';
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from "@mui/material/IconButton";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";

import AddContractRegister from './AddContractRegister';
import EditContractRegister from './EditContractRegister';

const getPathFromUrl = (url) => {
    try {
        const { pathname } = new URL(url);
        return pathname;
    } catch (e) {
        console.error("Eroare la extragerea URL-ului: ", e);
        return url;
    }
};

const getIconByExtension = (ext) => {
    switch (ext.toLowerCase()) {
        case ".pdf":
            return <PictureAsPdfIcon fontSize="small" style={{ color: "#d32f2f" }} />;
        case ".doc":
        case ".docx":
            return <DescriptionIcon fontSize="small" style={{ color: "#1976d2" }} />;
        case ".xls":
        case ".xlsx":
            return <TableChartIcon fontSize="small" style={{ color: "#388e3c" }} />;
        case ".png":
        case ".jpg":
        case ".jpeg":
        case ".gif":
            return <ImageIcon fontSize="small" style={{ color: "#f57c00" }} />;
        default:
            return <InsertDriveFileIcon fontSize="small" />;
    }
};

const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : Number(value);
    
    if (Number.isNaN(n)) return '';
    
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
};

const ContractRegisterList = ({ canCreate, canEdit, canDelete }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState(null);

    //preluarea contractelor
    useEffect(() => {
        const fetchContract = async () => {
            try {
                const response = await axiosInstance.get("/contract-register/");
                const storedData = response.data.sort((a, b) => b.id - a.id);
                setRows(storedData);
            } catch (error) {
                console.error("Error fetching contracts: ", error);
            } finally {
                setLoading(false);
            }
        };
        fetchContract();
    }, []);

    //stergerea unui contract
    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/contract-register/${selectedContractId}/`);
            setRows((prevRows) => prevRows.filter((row) => row.id !== selectedContractId));
        } catch (error) {
            console.error("Error deleting the contract: ", error);
        } finally {
            handleCloseDialog();
        }
    };

    //deschide dialogul de stergere
    const handleOpenDialog = (id) => {
        setSelectedContractId(id);
        setOpenDialog(true);
    };

    //inchide dialogul de stergere
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedContractId(null);
    };

    //formatare datei in tipul dd.mm.yyyy
    const formatDate = (dateString) => {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        } catch (error) {
            console.error("Eroare la formatarea datei: ", error, dateString);
            return dateString;
        }
    };

    const columns = [
        { 
            field: "contract_number", 
            headerName: "Contract Number", 
            minWidth: 140, 
            flex: 1, 
            headerClassName: "bold-header" 
        },
        { 
            field: "contract_type", 
            headerName: "Type", 
            minWidth: 100, 
            flex: 1, 
            headerClassName: "bold-header" 
        },
        {
            field: "contract_file",
            headerName: "Contract doc.",
            minWidth: 140,
            flex: 1,
            headerClassName: "bold-header",
            renderCell: (params) => {
                const filePath = params.value;
                const row = params.row;

                if (!filePath || typeof filePath !== "string") return null;

                const fileUrl = filePath.startsWith("/")
                    ? `${window.location.origin}${filePath}`
                    : `${window.location.origin}/${filePath}`;

                const fileName = filePath.split("/").pop();
                const fileExt = fileName?.includes(".")
                    ? fileName.substring(fileName.lastIndexOf("."))
                    : "";

                const icon = getIconByExtension(fileExt);

                const handleDownload = async (event) => {
                    event.preventDefault();

                    try {
                        if (!row || !row.id) {
                            console.error("❌ Row or row.id is undefined");
                            alert("Cannot download: Invalid document reference");
                            return;
                        }

                        const response = await axiosInstance.get(`/contract-register/${row.id}/download/`, {
                            responseType: 'blob',
                        });

                        const extensionMap = {
                            'application/pdf': 'pdf',
                            'application/msword': 'doc',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
                            'image/jpeg': 'jpg',
                            'image/png': 'png',
                        };

                        const extractFilename = (disposition) => {
                            if (!disposition) return null;

                            try {
                                const utf8Match = disposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
                                if (utf8Match) return decodeURIComponent(utf8Match[1].trim());

                                const quotedMatch = disposition.match(/filename="([^"]+)"/i);
                                if (quotedMatch) return quotedMatch[1];

                                const simpleMatch = disposition.match(/filename=([^;\n]+)/i);
                                if (simpleMatch) return simpleMatch[1].trim();
                            } catch (e) {
                                console.warn("Eroare la parsarea filename:", e);
                            }

                            return null;
                        };

                        let filename;
                        const disposition = response.headers && response.headers['content-disposition'];
                        filename = extractFilename(disposition);

                        if (!filename) {
                            filename = `document_${row.registration_nr || row.contract_number || row.id}`;
                        }

                        const mimeType = response.headers['content-type'];
                        const expectedExtension = extensionMap[mimeType];
                        const currentExtension = filename.split('.').pop().toLowerCase();

                        if (expectedExtension && currentExtension !== expectedExtension) {
                            filename = filename.replace(/\.[^/.]+$/, '') + `.${expectedExtension}`;
                        } else if (!filename.includes('.') && expectedExtension) {
                            filename += `.${expectedExtension}`;
                        }

                        const blob = new Blob([response.data], { type: mimeType });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', filename);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                    } catch (error) {
                        console.error('❌ Download failed:', error);

                        let message = 'Failed to download the document.';
                        if (error.response) {
                            if (error.response.status === 404) {
                                message = 'No attached document found for download.';
                            } else if (error.response.data?.error) {
                                message = error.response.data.error;
                            }
                        }
                        alert(message);
                    }
                };

                return (
                    <a
                        href={fileUrl}
                        onClick={handleDownload}
                        title={fileName}
                        className="flex items-center text-sm text-blue-600 hover:underline"
                        style={{
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <span>{fileExt}</span>
                        {icon}
                    </a>
                );
            }
        },
        { 
            field: "signin_date", 
            headerName: "Signing date", 
            minWidth: 135, 
            flex: 1, 
            headerClassName: "bold-header", 
            renderCell: (params) => formatDate(params.value) 
        },
        { 
            field: "end_date", 
            headerName: "Date final", 
            minWidth: 120, 
            flex: 1, 
            headerClassName: "bold-header", 
            renderCell: (params) => formatDate(params.value) 
        },
        { 
            field: "payment_terms", 
            headerName: "Payment term", 
            minWidth: 145, 
            flex: 1, 
            headerClassName: "bold-header" 
        },
        {
            field: "project_code_display", 
            headerName: "Project Code", 
            minWidth: 210, 
            flex: 1, 
            headerClassName: "bold-header",
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {params.value || "-"}
                </div>
            )
        },
        { 
            field: "subproject_code_display", 
            headerName: "Subproject Code", 
            minWidth: 150, 
            flex: 1, 
            headerClassName: "bold-header",
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {params.value || "-"}
                </div>
            )
        },
        {
            field: "supplier_name", 
            headerName: "Supplier", 
            minWidth: 180, 
            flex: 1, 
            headerClassName: "bold-header",
        },
        { 
            field: "object", 
            headerName: "Object",
            minWidth: 240, 
            flex: 1, 
            headerClassName: "bold-header" 
        },
        {
            field: "total_contract_display",
            headerName: "Total",
            minWidth: 160,
            flex: 1,
            headerClassName: "bold-header",
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '6px' }}>
                    <span>{formatNumber(params.value)}</span>
                    {params.row.currency_label && (
                        <span className="currency-badge">{params.row.currency_label}</span>
                    )}
                </div>)
        },
        {
            field: "total_contract_system_display",
            headerName: "Total System",
            minWidth: 160,
            flex: 1,
            headerClassName: "bold-header",
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '6px' }}>
                    <span>{formatNumber(params.value)}</span>
                    {params.row.system_currency_label && (
                        <span className="currency-badge">{params.row.system_currency_label}</span>
                    )}
                </div>)
        },
        ...(canEdit || canDelete ? [{
            field: "actions",
            headerName: "Action",
            flex: 1,
            minWidth: 140,
            headerClassName: "bold-header",
            headerAlign: "center",
            renderCell: (params) => (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    {canEdit &&
                        <IconButton
                            size='small'
                            color='primary'
                            onClick={() => navigate(`/contract-register/edit/${params.row.id}`)}
                        >
                            <EditIcon fontSize='small' />
                        </IconButton>
                    }

                    {canDelete &&
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDialog(params.row.id)}
                        >
                            <DeleteIcon fontSize='small' />
                        </IconButton>
                    }
                </div>
            ),
        }] : [])
    ];

    //afisam spinner-ul cat timp loading este true
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                gap: '20px'
            }}>
                <CircularProgress size={60} thickness={4} />
                <span style={{ fontSize: '18px', color: '#555' }}>Loading...</span>
            </div>
        );
    }

    return (
        <div className='contracts-pro'>
            <div className='header-container'>
                <h2 className='main-title'>PROCUREMENT CONTRACTS</h2>

                <div className='contracts-actions'>
                    {canCreate && (
                        <Button
                            className="create-contract-button"
                            variant='contained'
                            color='primary'
                            startIcon={<AddIcon />}
                            onClick={() => navigate("/contract-register/create")}
                        >
                            Create Contract
                        </Button>
                    )}
                </div>
            </div>

            <div className='contracts-counter'>
                Procurement Contracts
                <span className='count'>{rows.length}</span>
            </div>

            <div className="contracts-box-subcontainer contracts-page contracts-datagrid">
                <div className="contracts-data-grid-container full-width-height custom-contracts-datagrid">
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowHeight={() => "auto"}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "id", sort: "desc" }],
                            },
                            pagination: {
                                paginationModel: { pageSize: 12 },
                            },
                        }}
                        disableRowSelectionOnClick
                        sx={{
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: "1.4",
                                display: "flex",
                                alignItems: "center",
                                paddingTop: "3px",
                                paddingBottom: "3px",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                whiteSpace: "normal",
                            },
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-cell:focus-within": {
                                outline: "none",
                            },
                            "& .Mui-selected:hover": {
                                backgroundColor: "transparent !important",
                            },
                        }}
                        pageSizeOptions={[10, 12, 20, 25]}
                    />
                </div>
            </div>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                aria-labelledby='confirm-dialog-title'
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this contract?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color='primary'>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color='error' autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const ContractRegister = () => {
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasPermission = (path, checkPermission) => {
        const pagePermission = permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
        return pagePermission;
    };

    const canCreate = hasPermission('/contract-register', (page) => page.can_create);
    const canEdit = hasPermission('/contract-register', (page) => page.can_edit);
    const canDelete = hasPermission('/contract-register', (page) => page.can_delete);

    return (
        <Routes>
            <Route path="/" element={<ContractRegisterList canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />} />
            <Route path="create" element={canCreate ? <AddContractRegister /> : <NotAuthorized />} />
            <Route path="edit/:id" element={canEdit ? <EditContractRegister /> : <NotAuthorized />} />
        </Routes>
    );
};

export default ContractRegister;